'use es6';

import I18n from 'I18n';
import { findIndex, uniqueId, some, isEmpty } from 'underscore';
import { updateFormFieldGroup, updateFormFieldDefinition } from 'FormsUI/actions/FormDefinition';
import { updateSelectedFormItem, resetSelectedFormItem } from 'FormsUI/actions/FormSelection';
import { handleShowModal } from 'FormsUI/actions/PropertyCreation';
import { getObjectTypeIdValue } from 'FormsCommonComponents/form-definition/utils/FormFieldUtils';
import { getFormFieldGroups, isNewPropertyNeeded, getPaymentField } from 'FormsUI/selectors/FormDefinition';
import { getMainFormFieldGroups } from 'FormsUI/selectors/FormFieldsProperties';
import { getPropertyOrCustomProperty, isPropertyRequiredForObject } from 'FormsUI/selectors/customObjects';
import { reorderLinkedRichText } from 'FormsUI/actions/FormDragDropReorder';
import updateItemInArray from 'forms-utils-lib/utils/array/updateItemInArray';
import addItemToArray from 'forms-utils-lib/utils/array/addItemToArray';
import removeItemFromArray from 'forms-utils-lib/utils/array/removeItemFromArray';
import { trackEditorInteraction } from 'FormsUI/utils/TrackingUtils';
import createFormFieldWithOptions from 'forms-utils-lib/formField/utils/createFormFieldWithOptions';
import createNewFormFieldGroup from 'forms-utils-lib/formFieldGroup/utils/createFormFieldGroup';
import { scrollMainPanel } from 'FormsUI/utils/AnimationUtils';
import { prependFields } from 'ui-forms-components/constants/FieldTypeMapping';
import { RichTextFields, RichTextFieldTypes } from 'ui-forms-components/constants/FieldTypeMapping';
import { requiredTicketPropertiesForConversations } from 'forms-utils-lib/constants/Properties';
import { isTicketProperty } from 'FormsCommonComponents/utils/PropertyObjectTypes';
import { showDisableTicketWarningDialog } from './DialogsAndPanels';
import generateDefaultRichTextContent from 'FormsUI/utils/generateDefaultRichTextContent';
import { updatePrivateInternalIdsOnFieldGroups } from 'FormsUI/utils/FormFieldsUtils';
import { getUserPreferredLocale } from 'FormsUI/selectors/UserPreferences';
import { SPLIT_ROW } from 'FormsUI/constants/TrackingInteractionKeys/Editor/FormTab';
import { addValidationErrorIfNeeded, removeValidationErrorIfNeeded } from 'FormsUI/actions/FormDefinition';
import { TabValidationsKeys, ValidationKeys, ValidationType } from 'FormsUI/constants/FormFieldValidations';
import updateFormFieldKey from 'forms-utils-lib/formField/utils/updateFormFieldKey';
import FormFieldKeys from 'forms-utils-lib/constants/formField/FormFieldKeys';
import { addFormFieldGroup as _addFormFieldGroup } from 'FormsUI/utils/FormFieldGroupUtils';
import { disablePayments } from './Payments';
import { UnknownProperties } from 'forms-utils-lib/constants/Properties';
function createAction(name) {
  return `@@formDragDrop/${name}`;
}
export const SET_DRAGGED_ITEM = createAction('SET_DRAGGED_ITEM');
export const ADD_NEW_FORM_FIELD_GROUP = createAction('ADD_NEW_FORM_FIELD_GROUP');
export const APPEND_NEW_FORM_FIELD_GROUP = createAction('APPEND_NEW_FORM_FIELD_GROUP');
export const ADD_NEW_FORM_FIELD = createAction('ADD_NEW_FORM_FIELD');
export const REMOVE_FORM_FIELD = createAction('REMOVE_FORM_FIELD');
export const REORDER_FORM_FIELD = createAction('REORDER_FORM_FIELD');
export const ADD_NEW_RICH_TEXT = createAction('ADD_NEW_RICH_TEXT');
export const REMOVE_RICH_TEXT = createAction('REMOVE_RICH_TEXT');
export const REORDER_RICH_TEXT = createAction('REORDER_RICH_TEXT');
const CREATE_NEW_PROPERTY_IF_NEEDED = createAction('CREATE_NEW_PROPERTY_IF_NEEDED');
function setDraggedItem(draggedItem) {
  return {
    type: SET_DRAGGED_ITEM,
    draggedItem
  };
}
export function addNewFormFieldGroup(formFieldGroups, variant = '') {
  return {
    type: ADD_NEW_FORM_FIELD_GROUP,
    formFieldGroups,
    variant
  };
}
function addNewFormField(formFieldGroups) {
  return {
    type: ADD_NEW_FORM_FIELD,
    formFieldGroups
  };
}
export function deleteFormField(formFieldGroups, variant = '') {
  return {
    type: REMOVE_FORM_FIELD,
    formFieldGroups,
    variant
  };
}
function reorderFormField(formFieldGroups) {
  return {
    type: REORDER_FORM_FIELD,
    formFieldGroups
  };
}
export function addNewRichText(formFieldGroups) {
  return {
    type: ADD_NEW_RICH_TEXT,
    formFieldGroups
  };
}
export function deleteRichText(formFieldGroups) {
  return {
    type: REMOVE_RICH_TEXT,
    formFieldGroups
  };
}
export function reorderExistingRichText(formFieldGroups) {
  return {
    type: REORDER_RICH_TEXT,
    formFieldGroups
  };
}
export function updateDraggedItem(draggedItem) {
  return dispatch => {
    dispatch(resetSelectedFormItem());
    dispatch(setDraggedItem(draggedItem));
  };
}
const _createNewFormField = (getState, {
  property,
  isQueued
} = {}) => {
  const state = getState();
  const isRequired = isPropertyRequiredForObject(state, property);
  const locale = getUserPreferredLocale(state);
  return createFormFieldWithOptions(property, isQueued, isRequired, locale);
};
const createNewPropertyIfNeeded = fieldType => (dispatch, getState) => {
  dispatch({
    type: CREATE_NEW_PROPERTY_IF_NEEDED
  });
  if (isNewPropertyNeeded(getState())) {
    dispatch(handleShowModal(fieldType));
  }
};
export function addRichText(targetFormFieldGroupIndex, content) {
  return (dispatch, getState) => {
    const defaultContent = content || `<p>${I18n.text('editor.form.formFields.defaultValues.richText')}</p>`;
    const formFieldGroups = getFormFieldGroups(getState());
    const formFieldGroup = formFieldGroups[targetFormFieldGroupIndex];
    const updatedFormFieldGroup = Object.assign({}, formFieldGroup, {
      richText: Object.assign({}, formFieldGroup.richText, {
        content: defaultContent,
        privateInternalId: Number(uniqueId())
      })
    });
    const newFormFieldGroups = updatePrivateInternalIdsOnFieldGroups(updateItemInArray(formFieldGroups, targetFormFieldGroupIndex, updatedFormFieldGroup));
    dispatch(addNewRichText(newFormFieldGroups));
  };
}
export function addFormFieldGroup(propertyId, formFieldGroupIndex, {
  isQueued = false,
  newFieldType,
  richTextContent,
  showConfigPanel,
  variant,
  isPageBreak
} = {}) {
  return (dispatch, getState) => {
    const state = getState();
    const isRichText = !isEmpty(richTextContent);
    const formFieldGroups = getFormFieldGroups(state, variant);
    const paymentLinkFormField = getPaymentField(state);
    let newFormFieldGroup;
    if (isRichText && isQueued) {
      dispatch(addRichText(formFieldGroupIndex, richTextContent));
      return;
    } else if (isRichText) {
      newFormFieldGroup = createNewFormFieldGroup({
        default: !isQueued,
        isSmartGroup: isQueued,
        richText: {
          content: richTextContent,
          type: newFieldType === RichTextFields.IMAGE ? 'IMAGE' : 'TEXT'
        }
      });
    } else if (isPageBreak) {
      newFormFieldGroup = createNewFormFieldGroup({
        default: true,
        isSmartGroup: false,
        richText: {
          content: '',
          type: 'TEXT'
        },
        isPageBreak
      });
    } else {
      const property = getPropertyOrCustomProperty(getState(), propertyId);
      if (property) {
        const newFormField = _createNewFormField(getState, {
          property,
          isQueued,
          fieldType: newFieldType
        });
        if (propertyId && newFormField.fieldType && showConfigPanel) {
          dispatch(updateSelectedFormItem(newFormField.fieldType, propertyId));
        }
        newFormFieldGroup = createNewFormFieldGroup({
          field: newFormField,
          default: !isQueued,
          isSmartGroup: isQueued
        });
      }
    }
    if (newFormFieldGroup) {
      const updatedFormFieldGroups = _addFormFieldGroup(formFieldGroups, formFieldGroupIndex, newFormFieldGroup, {
        paymentLinkFormField
      });
      dispatch(addNewFormFieldGroup(updatedFormFieldGroups, variant));
      dispatch(removeValidationErrorIfNeeded(TabValidationsKeys.FORM, ValidationKeys.NO_FIELDS, ValidationType.REQUIRED));
      if (!isRichText) {
        dispatch(removeValidationErrorIfNeeded(TabValidationsKeys.FORM, ValidationKeys.NO_FIELDS, ValidationType.REQUIRED));
        dispatch(createNewPropertyIfNeeded(newFieldType));
      }
    }
  };
}
export function appendFormFieldGroup(propertyId, {
  scroll = false,
  showConfigPanel = true,
  fieldType,
  variant,
  isPageBreak
} = {}) {
  return (dispatch, getState) => {
    const formFieldGroups = getMainFormFieldGroups(getState(), variant); // append after last main form field group (not counting queued fields)
    dispatch(addFormFieldGroup(propertyId, formFieldGroups.length, Object.assign({
      newFieldType: fieldType,
      showConfigPanel,
      isPageBreak,
      variant
    }, !isEmpty(RichTextFieldTypes[fieldType]) && {
      richTextContent: generateDefaultRichTextContent(fieldType)
    })));
    if (scroll && !isEmpty(propertyId)) {
      scrollMainPanel(`[id="form-field-${propertyId}"]`);
    }
  };
}
const prependFormFieldGroup = (propertyId, {
  scroll = false,
  showConfigPanel = true,
  fieldType
} = {}) => dispatch => {
  dispatch(addFormFieldGroup(propertyId, 0, Object.assign({
    newFieldType: fieldType,
    showConfigPanel
  }, !isEmpty(RichTextFieldTypes[fieldType]) && {
    richTextContent: generateDefaultRichTextContent(fieldType)
  })));
  if (scroll && !isEmpty(propertyId)) {
    scrollMainPanel(`[id="form-field-${propertyId}"]`);
  } else {
    scrollMainPanel();
  }
};
export const handleFieldListClick = options => dispatch => {
  const isPageBreak = options.fieldType === 'page';
  if (Object.prototype.hasOwnProperty.call(prependFields, options.fieldType)) {
    return dispatch(prependFormFieldGroup(null, Object.assign({}, options, {
      isPageBreak
    })));
  }
  return dispatch(appendFormFieldGroup(null, Object.assign({}, options, {
    isPageBreak
  })));
};
export function addFormField(propertyId, formFieldGroupIndex, formFieldIndex, {
  newFieldType
} = {}) {
  return (dispatch, getState) => {
    const state = getState();
    const formFieldGroups = getFormFieldGroups(state);
    const property = getPropertyOrCustomProperty(state, propertyId);
    if (property) {
      const newFormField = _createNewFormField(getState, {
        property,
        isQueued: false,
        fieldType: newFieldType
      });
      const oldFormFieldGroup = formFieldGroups[formFieldGroupIndex];
      const updatedFormFieldGroup = Object.assign({}, oldFormFieldGroup, {
        fields: addItemToArray(oldFormFieldGroup.fields, formFieldIndex, newFormField)
      });
      const updatedFormFieldGroups = updatePrivateInternalIdsOnFieldGroups(updatePrivateInternalIdsOnFieldGroups(updateItemInArray(formFieldGroups, formFieldGroupIndex, updatedFormFieldGroup)));
      dispatch(addNewFormField(updatedFormFieldGroups));
      dispatch(createNewPropertyIfNeeded(newFieldType));
      dispatch(removeValidationErrorIfNeeded(TabValidationsKeys.FORM, ValidationKeys.NO_FIELDS, ValidationType.REQUIRED));
      const groupHasAnySmartField = some(updatedFormFieldGroup.fields, field => field.isSmartField);
      if (groupHasAnySmartField) {
        dispatch(updateFormFieldGroup(formFieldGroupIndex, {
          isSmartGroup: true
        }));
        updatedFormFieldGroup.fields.forEach(field => {
          dispatch(updateFormFieldDefinition(field.name, getObjectTypeIdValue(field.objectTypeId, field.propertyObjectType), 'isSmartField', true));
        });
      }
    }
  };
}
export function removeFormField(sourceFormFieldGroupIndex, name, objectTypeId) {
  return (dispatch, getState) => {
    if (name === UnknownProperties.PAYMENT_LINK) {
      return void dispatch(disablePayments());
    }

    // Handle special ticket fields required for Conversations
    if (isTicketProperty(objectTypeId) && requiredTicketPropertiesForConversations.indexOf(name) >= 0) {
      return void dispatch(showDisableTicketWarningDialog());
    }
    const formFieldGroups = getFormFieldGroups(getState());
    const sourceFormFieldGroup = formFieldGroups[sourceFormFieldGroupIndex];
    const sourceFormFieldIndex = findIndex(sourceFormFieldGroup.fields, {
      name,
      objectTypeId
    });
    let newFormFieldGroups;
    if (sourceFormFieldGroup.fields.length > 1) {
      const updatedSourceFormFieldGroup = Object.assign({}, sourceFormFieldGroup, {
        fields: removeItemFromArray(sourceFormFieldGroup.fields, sourceFormFieldIndex)
      });
      newFormFieldGroups = updateItemInArray(formFieldGroups, sourceFormFieldGroupIndex, updatedSourceFormFieldGroup);
    } else {
      newFormFieldGroups = removeItemFromArray(formFieldGroups, sourceFormFieldGroupIndex);
    }
    dispatch(deleteFormField(updatePrivateInternalIdsOnFieldGroups(newFormFieldGroups)));
    if (newFormFieldGroups.length === 0) {
      dispatch(addValidationErrorIfNeeded(TabValidationsKeys.FORM, ValidationKeys.NO_FIELDS, ValidationType.REQUIRED));
    } else {
      dispatch(removeValidationErrorIfNeeded(TabValidationsKeys.FORM, ValidationKeys.NO_FIELDS, ValidationType.REQUIRED));
    }
    return null;
  };
}
export function removeNullPropertyFormField() {
  return (dispatch, getState) => {
    const formFieldGroups = getFormFieldGroups(getState());
    const resetGroups = formFieldGroups.reduce((accum, group) => {
      const newGroup = Object.assign({}, group, {
        fields: group.fields.filter(({
          name
        }) => name)
      });
      const shouldRemoveGroup = isEmpty(newGroup.fields) && isEmpty(newGroup.richText.content) && !newGroup.isPageBreak;
      if (shouldRemoveGroup) {
        return accum;
      }

      /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
      return [...accum, newGroup];
    }, []);
    dispatch(deleteFormField(resetGroups));
  };
}
export function populateNullPropertyFormField(property) {
  return (dispatch, getState) => {
    const formFieldGroups = getFormFieldGroups(getState());
    const newGroups = formFieldGroups.map(group => {
      return Object.assign({}, group, {
        fields: group.fields.map(field => {
          if (field.name) {
            return field;
          }
          return _createNewFormField(getState, {
            property,
            fieldType: field.fieldType,
            isQueued: !group.default
          });
        })
      });
    });
    dispatch(addNewFormField(newGroups));
  };
}
export function moveFormFieldIntoNewFormFieldGroup(sourceFormFieldGroupIndex, formFieldName, formFieldPropertyObjectType, targetFormFieldGroupIndex, {
  isQueued = false
} = {}) {
  return (dispatch, getState) => {
    const formFieldGroups = getFormFieldGroups(getState());
    const sourceFormFieldGroup = formFieldGroups[sourceFormFieldGroupIndex];
    const sourceFormFieldIndex = findIndex(sourceFormFieldGroup.fields, {
      name: formFieldName,
      propertyObjectType: formFieldPropertyObjectType
    });
    let sourceFormField = sourceFormFieldGroup.fields[sourceFormFieldIndex];
    if (isQueued) {
      sourceFormField = updateFormFieldKey(sourceFormField, FormFieldKeys.IS_SMART_FIELD, true);
    }
    const newFormFieldGroup = sourceFormFieldGroup.fields.length === 1 ? Object.assign(sourceFormFieldGroup, {
      default: !isQueued,
      isSmartGroup: sourceFormField.isSmartField || isQueued,
      fields: [sourceFormField]
    }) : createNewFormFieldGroup({
      field: sourceFormField,
      default: !isQueued,
      isSmartGroup: sourceFormField.isSmartField || isQueued
    });
    let newFormFieldGroups;
    if (sourceFormFieldGroup.fields.length > 1) {
      const updatedSourceFormFieldGroup = Object.assign({}, sourceFormFieldGroup, {
        fields: removeItemFromArray(sourceFormFieldGroup.fields, sourceFormFieldIndex)
      });
      newFormFieldGroups = updateItemInArray(formFieldGroups, sourceFormFieldGroupIndex, updatedSourceFormFieldGroup);
      newFormFieldGroups = addItemToArray(newFormFieldGroups, targetFormFieldGroupIndex, newFormFieldGroup);
    } else {
      if (targetFormFieldGroupIndex > sourceFormFieldGroupIndex) {
        newFormFieldGroups = addItemToArray(formFieldGroups, targetFormFieldGroupIndex, newFormFieldGroup);
        newFormFieldGroups = removeItemFromArray(newFormFieldGroups, sourceFormFieldGroupIndex);
      } else {
        newFormFieldGroups = removeItemFromArray(formFieldGroups, sourceFormFieldGroupIndex);
        newFormFieldGroups = addItemToArray(newFormFieldGroups, targetFormFieldGroupIndex, newFormFieldGroup);
      }
    }
    dispatch(reorderFormField(updatePrivateInternalIdsOnFieldGroups(newFormFieldGroups)));
  };
}
export function reorderRichText(sourceFormFieldGroupIndex, targetFormFieldGroupIndex, {
  isQueued = false
}) {
  return (dispatch, getState) => {
    const formFieldGroups = getFormFieldGroups(getState());
    const sourceGroup = formFieldGroups[sourceFormFieldGroupIndex];
    const isLinkedRichText = !isEmpty(sourceGroup.fields);
    const newFormFieldGroups = formFieldGroups.reduce((accum, group, index) => {
      // Create rich text group or merge if queued
      if (index === targetFormFieldGroupIndex) {
        return isQueued ? [...accum, Object.assign({}, group, {
          richText: sourceGroup.richText
        })] : [...accum, isLinkedRichText ? Object.assign({}, sourceGroup, {
          fields: [],
          default: true
        }) : sourceGroup, group];
      }

      // Remove rich text if reorded form group that had it linked
      if (index === sourceFormFieldGroupIndex) {
        return [...accum, ...(isLinkedRichText ? [Object.assign({}, group, {
          richText: {
            content: ''
          }
        })] : [])];
      }
      if (index === formFieldGroups.length - 1 && targetFormFieldGroupIndex >= formFieldGroups.length) {
        return [...accum, group, sourceGroup];
      }

      /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
      return [...accum, group];
    }, []);
    return dispatch(reorderExistingRichText(updatePrivateInternalIdsOnFieldGroups(newFormFieldGroups)));
  };
}
export const appendRichText = () => (dispatch, getState) => {
  const formFieldGroups = getMainFormFieldGroups(getState()); // append to last main form field (not counting queued fields)
  dispatch(addRichText(formFieldGroups.length - 1));
};
export function removeRichText(sourceFormFieldGroupIndex) {
  return (dispatch, getState) => {
    const formFieldGroups = getFormFieldGroups(getState());
    const sourceFormFieldGroup = formFieldGroups[sourceFormFieldGroupIndex];
    // If not linked richText
    if (isEmpty(sourceFormFieldGroup.fields)) {
      const newGroups = updatePrivateInternalIdsOnFieldGroups(removeItemFromArray(formFieldGroups, sourceFormFieldGroupIndex));
      return dispatch(deleteRichText(newGroups));
    }
    const newFormFieldGroup = Object.assign({}, sourceFormFieldGroup, {
      richText: Object.assign({}, sourceFormFieldGroup.richText, {
        content: '',
        privateInternalId: null
      })
    });
    const newFormFieldGroups = updatePrivateInternalIdsOnFieldGroups(updateItemInArray(formFieldGroups, sourceFormFieldGroupIndex, newFormFieldGroup));
    return dispatch(deleteRichText(newFormFieldGroups));
  };
}
export function moveFormFieldIntoExistingFormFieldGroup(sourceFormFieldGroupIndex, formFieldName, formFieldPropertyObjectType, targetFormFieldGroupIndex, targetFormFieldIndex) {
  return (dispatch, getState) => {
    const numFieldsInSourceGroup = getFormFieldGroups(getState())[sourceFormFieldGroupIndex].fields.length;
    if (numFieldsInSourceGroup === 1) {
      dispatch(reorderLinkedRichText(sourceFormFieldGroupIndex, targetFormFieldGroupIndex));
    }
    const formFieldGroups = getFormFieldGroups(getState());
    const targetFormFieldGroup = formFieldGroups[targetFormFieldGroupIndex];
    const sourceFormFieldGroup = formFieldGroups[sourceFormFieldGroupIndex];
    const sourceFormFieldIndex = findIndex(sourceFormFieldGroup.fields, {
      name: formFieldName,
      propertyObjectType: formFieldPropertyObjectType
    });
    const sourceFormField = sourceFormFieldGroup.fields[sourceFormFieldIndex];
    if (sourceFormFieldGroupIndex === targetFormFieldGroupIndex) {
      let updatedFormFields;
      if (targetFormFieldIndex > sourceFormFieldIndex) {
        updatedFormFields = removeItemFromArray(addItemToArray(sourceFormFieldGroup.fields, targetFormFieldIndex, sourceFormField), sourceFormFieldIndex);
      } else {
        updatedFormFields = addItemToArray(removeItemFromArray(sourceFormFieldGroup.fields, sourceFormFieldIndex), targetFormFieldIndex, sourceFormField);
      }
      const updatedFormFieldGroup = Object.assign({}, sourceFormFieldGroup, {
        fields: updatedFormFields
      });
      const updatedFormFieldGroups = updatePrivateInternalIdsOnFieldGroups(updateItemInArray(formFieldGroups, sourceFormFieldGroupIndex, updatedFormFieldGroup));
      dispatch(reorderFormField(updatedFormFieldGroups));
      return;
    }
    const updatedTargetFormFieldGroup = Object.assign({}, targetFormFieldGroup, {
      fields: addItemToArray(targetFormFieldGroup.fields, targetFormFieldIndex, sourceFormField)
    });
    trackEditorInteraction(SPLIT_ROW, {
      numberFields: updatedTargetFormFieldGroup.fields.length
    });
    let newFormFieldGroups = updateItemInArray(formFieldGroups, targetFormFieldGroupIndex, updatedTargetFormFieldGroup);
    let updatedTargetIndex = targetFormFieldGroupIndex;
    if (sourceFormFieldGroup.fields.length > 1) {
      const updatedSourceFormFieldGroup = Object.assign({}, sourceFormFieldGroup, {
        fields: removeItemFromArray(sourceFormFieldGroup.fields, sourceFormFieldIndex)
      });
      newFormFieldGroups = updateItemInArray(newFormFieldGroups, sourceFormFieldGroupIndex, updatedSourceFormFieldGroup);
    } else {
      newFormFieldGroups = removeItemFromArray(newFormFieldGroups, sourceFormFieldGroupIndex);
      if (sourceFormFieldGroupIndex < targetFormFieldGroupIndex) {
        updatedTargetIndex -= 1;
      }
    }
    dispatch(reorderFormField(updatePrivateInternalIdsOnFieldGroups(newFormFieldGroups)));
    const groupHasAnySmartField = some(updatedTargetFormFieldGroup.fields, field => field.isSmartField);
    if (groupHasAnySmartField) {
      dispatch(updateFormFieldGroup(updatedTargetIndex, {
        isSmartGroup: true
      }));
      updatedTargetFormFieldGroup.fields.forEach(field => {
        dispatch(updateFormFieldDefinition(field.name, getObjectTypeIdValue(field.objectTypeId, field.propertyObjectType), 'isSmartField', true));
      });
    }
  };
}